import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowRight, HandHelping } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/shared/components/ui/button';
import { Combobox } from '@/shared/components/ui/combobox';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/dialog';
import { Input } from '@/shared/components/ui/input';
import { Label } from '@/shared/components/ui/label';
import { cn } from '@/shared/lib/cn';
import {
  listActiveContactos,
  type ContactoItem,
} from '@/shared/api/controllers/contactos';
import { registrarPrestamo, type InventarioRow } from '../api';

/**
 * Register a loan: move units from Disponible to Prest/Show and write
 * a `22.Prestamos` row tied to a cliente. Cliente + fecha préstamo are
 * required — matches PowerApps's PopUp_PrestamoPersonal which gates
 * the confirm button on both. Loans without a recipient have no
 * tracking value.
 *
 * No fecha-fin is captured: PowerApps's Patch to `22.Prestamos` does
 * not set `FechaPrestamoFin_PPRES`.
 */
export function RegistrarPrestamoDialog({
  row,
  onClose,
}: {
  row: InventarioRow;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const [cantidadRaw, setCantidadRaw] = useState('');
  const [cliente, setCliente] = useState<ContactoItem | null>(null);
  const [fechaPrestamo, setFechaPrestamo] = useState(todayDdMmYyyy);

  const contactosQ = useQuery({
    queryKey: ['contactos', 'active'],
    queryFn: listActiveContactos,
    staleTime: 5 * 60_000,
  });

  const cantidad = parseCantidad(cantidadRaw);
  const disponible = row.buckets.disponible;
  const exceeds = cantidad !== null && cantidad > disponible + 0.0001;
  const fechaPrestamoValid = /^\d{2}\/\d{2}\/\d{4}$/.test(fechaPrestamo.trim());
  const canSubmit =
    cantidad !== null &&
    cantidad > 0 &&
    !exceeds &&
    disponible > 0 &&
    cliente !== null &&
    fechaPrestamoValid;

  const mutation = useMutation({
    mutationFn: () =>
      registrarPrestamo(row.id, {
        cantidad: cantidad ?? 0,
        clienteId: cliente?.id ?? '',
        cliente:
          cliente?.fields.NombreApellido_C?.trim() ||
          `${cliente?.fields.Nombre_C ?? ''} ${cliente?.fields.Apellido_C ?? ''}`.trim() ||
          '—',
        fechaPrestamo: fechaPrestamo.trim(),
      }),
    onSuccess: (r) => {
      if (r.outcome === 'insufficient_disponible') {
        toast.error(r.message);
        return;
      }
      toast.success(`${r.cantidad} un. prestadas`);
      qc.invalidateQueries({ queryKey: ['inventario'] });
      onClose();
    },
    onError: () => toast.error('No se pudo registrar el préstamo'),
  });

  return (
    <Dialog open onOpenChange={(o) => !o && !mutation.isPending && onClose()}>
      <DialogContent
        className="max-w-md"
        processing={mutation.isPending && 'Registrando préstamo…'}
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <HandHelping className="h-4 w-4" aria-hidden />
            Registrar Préstamo
          </DialogTitle>
          <DialogDescription>
            {row.fields.Articulo_I || 'artículo'} · Disponible:{' '}
            <strong>{disponible}</strong> un.
          </DialogDescription>
        </DialogHeader>
        <DialogBody className="space-y-4">
          <div className="flex items-center justify-between gap-3 rounded-lg border bg-muted/30 px-3 py-2">
            <Bucket label="Disponible" value={disponible} tone="green" />
            <ArrowRight className="h-5 w-5 text-muted-foreground" aria-hidden />
            <Bucket label="Prest/Show" value={row.buckets.prestShow} tone="rose" />
          </div>

          <div className="space-y-1">
            <Label className="text-sm">
              Cliente <span className="text-destructive">*</span>
            </Label>
            <Combobox<ContactoItem>
              value={cliente}
              onChange={setCliente}
              items={contactosQ.data ?? []}
              getKey={(c) => c.id}
              getLabel={(c) =>
                c.fields.NombreApellido_C?.trim() ||
                `${c.fields.Nombre_C ?? ''} ${c.fields.Apellido_C ?? ''}`.trim() ||
                '—'
              }
              getSublabel={(c) => c.fields.CUIT_C?.trim() || c.fields.Mail_C || null}
              matcher={(c, q) => {
                const full = (
                  c.fields.NombreApellido_C ||
                  `${c.fields.Nombre_C ?? ''} ${c.fields.Apellido_C ?? ''}`
                ).toLowerCase();
                return (
                  full.includes(q) ||
                  (c.fields.CUIT_C ?? '').toLowerCase().includes(q) ||
                  (c.fields.Mail_C ?? '').toLowerCase().includes(q)
                );
              }}
              placeholder={
                contactosQ.isPending ? 'Cargando clientes…' : 'Seleccionar cliente'
              }
              disabled={contactosQ.isPending}
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label htmlFor="pp-cantidad" className="text-sm">
                Cantidad <span className="text-destructive">*</span>
              </Label>
              <Input
                id="pp-cantidad"
                type="number"
                inputMode="decimal"
                min={0}
                max={disponible}
                step="0.01"
                placeholder={`máx ${disponible}`}
                value={cantidadRaw}
                onChange={(e) => setCantidadRaw(e.target.value)}
                className={cn('tabular-nums', exceeds && 'border-destructive')}
              />
              {exceeds && (
                <p className="text-xs text-destructive">Excede Disponible ({disponible}).</p>
              )}
            </div>
            <div className="space-y-1">
              <Label htmlFor="pp-fecha" className="text-sm">
                Fecha préstamo <span className="text-destructive">*</span>
              </Label>
              <Input
                id="pp-fecha"
                type="text"
                inputMode="numeric"
                placeholder="dd/mm/aaaa"
                value={fechaPrestamo}
                onChange={(e) => setFechaPrestamo(e.target.value)}
                className={cn(!fechaPrestamoValid && 'border-destructive')}
              />
              {!fechaPrestamoValid && (
                <p className="text-xs text-destructive">Formato dd/mm/aaaa.</p>
              )}
            </div>
          </div>

          {cantidad !== null && cantidad > 0 && !exceeds && (
            <div className="rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-800">
              Después: Disponible {disponible - cantidad} · Prest/Show{' '}
              {row.buckets.prestShow + cantidad}
            </div>
          )}
        </DialogBody>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} disabled={mutation.isPending}>
            Cancelar
          </Button>
          <Button
            onClick={() => mutation.mutate()}
            disabled={!canSubmit || mutation.isPending}
            className="gap-2"
          >
            <HandHelping className="h-4 w-4" aria-hidden />
            Registrar préstamo
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Bucket({
  label,
  value,
  tone,
}: {
  label: string;
  value: number;
  tone: 'green' | 'rose';
}) {
  const cls =
    tone === 'green'
      ? 'bg-emerald-100 text-emerald-700'
      : 'bg-rose-100 text-rose-700';
  return (
    <div className="flex flex-1 flex-col items-center">
      <span className="text-[11px] uppercase tracking-wide text-muted-foreground">
        {label}
      </span>
      <span
        className={cn(
          'mt-1 inline-flex min-w-12 justify-center rounded-md px-2 py-0.5 text-sm font-semibold tabular-nums',
          cls,
        )}
      >
        {value}
      </span>
    </div>
  );
}

function parseCantidad(raw: string): number | null {
  const trimmed = raw.trim();
  if (!trimmed) return null;
  const n = Number(trimmed.replace(',', '.'));
  return Number.isFinite(n) && n >= 0 ? n : null;
}

function todayDdMmYyyy(): string {
  const d = new Date();
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  return `${dd}/${mm}/${d.getFullYear()}`;
}
